// Онбординг-роадмап для нового заведения.
//
// Ведёт владельца по шагам запуска: профиль → меню (категории + блюда) → касса.
// Прогресс ВЫЧИСЛЯЕТСЯ из существующих API — новых полей в БД не нужно.
//
// Реальное время шкалы:
//   • ключи запросов совпадают со страницами (admin/cat, admin/dishes,
//     admin/devices) — поэтому добавление категории/блюда на /menu сразу
//     инвалидирует кэш и шаг закрывается без перезагрузки;
//   • refetchOnMount:'always' + refetchOnWindowFocus — при возврате на
//     дашборд (после привязки кассы на /devices и т.п.) данные пересчитываются.
// Состояние «свёрнуто/скрыто» хранится в localStorage по каждому заведению.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  PartyPopper,
  Store,
  Tablet,
  Utensils,
  ArrowRight,
  X,
} from 'lucide-react';

import {
  listEstablishments,
  listCategories,
  listDishes,
  listDevices,
} from '@/services/admin';
import { useEstablishmentStore } from '@/store/establishment.store';

// Прямая ссылка на страницу загрузок лендинга (Android-сборка «Resstro Касса»).
const DOWNLOADS_URL = 'https://resstro.ru/downloads';

// Общие опции для всех запросов чеклиста — дают актуальность шкалы в реальном
// времени при навигации внутри админки.
const LIVE_QUERY = {
  staleTime: 5_000,
  refetchOnMount: 'always' as const,
  refetchOnWindowFocus: true,
};

type StepAction = {
  label: string;
  to?: string; // внутренний роут (react-router Link)
  href?: string; // внешняя ссылка (<a target=_blank>)
  primary?: boolean;
  onClick?: () => void; // побочный эффект при клике (напр. отметить шаг)
};

type StepCheck = { label: string; done: boolean };

type Step = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  done: boolean;
  checks?: StepCheck[];
  actions: StepAction[];
};

function lsKey(estId: string, suffix: string) {
  return `resstro_onboarding:${suffix}:${estId}`;
}

export default function OnboardingChecklist() {
  const estId = useEstablishmentStore((s) => s.currentId);

  const establishments = useQuery({
    queryKey: ['admin/establishments'],
    queryFn: listEstablishments,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const categories = useQuery({
    queryKey: ['admin/cat'],
    queryFn: listCategories,
    ...LIVE_QUERY,
  });
  // Префикс ['admin/dishes', …] — чтобы invalidateQueries(['admin/dishes'])
  // из формы блюда автоматически освежало эту шкалу.
  const dishes = useQuery({
    queryKey: ['admin/dishes', 'onboarding'],
    queryFn: () => listDishes(),
    ...LIVE_QUERY,
  });
  const devices = useQuery({
    queryKey: ['admin/devices', 'KASSA'],
    queryFn: () => listDevices('KASSA'),
    ...LIVE_QUERY,
  });

  const queries = [establishments, categories, dishes, devices];
  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  const currentEst = useMemo(() => {
    const list = establishments.data ?? [];
    if (!list.length) return null;
    return list.find((e) => e.id === estId) ?? list[0];
  }, [establishments.data, estId]);

  // Шаг «касса» закрываем по факту перехода к подключению устройства
  // (пользователю достаточно открыть экран привязки) ИЛИ если реально привязан
  // хотя бы один девайс. Флаг визита храним per-establishment в localStorage.
  const visitKey = currentEst ? lsKey(currentEst.id, 'device_visited') : '';
  const [deviceVisited, setDeviceVisited] = useState(false);
  useEffect(() => {
    setDeviceVisited(visitKey ? localStorage.getItem(visitKey) === '1' : false);
  }, [visitKey]);
  const markDeviceVisited = () => {
    if (visitKey) localStorage.setItem(visitKey, '1');
    setDeviceVisited(true);
  };

  const steps: Step[] = useMemo(() => {
    const catCount = categories.data?.length ?? 0;
    const dishCount = dishes.data?.length ?? 0;

    const profileDone = !!(currentEst && (currentEst.logoUrl || currentEst.address));
    const menuDone = catCount > 0 && dishCount > 0;
    const deviceDone = (devices.data?.results ?? []).some((d) => !d.revoked);

    // Меню: ведём по порядку — без категории блюдо не создать.
    const menuActions: StepAction[] =
      catCount === 0
        ? [
            { label: 'Создать категорию', to: '/menu?tab=categories', primary: true },
            { label: 'Сканировать меню', to: '/menu?tab=scanner' },
          ]
        : [
            { label: 'Добавить блюдо', to: '/menu?tab=dishes', primary: true },
            { label: 'Сканировать меню', to: '/menu?tab=scanner' },
            { label: 'Категории', to: '/menu?tab=categories' },
          ];

    return [
      {
        id: 'profile',
        title: 'Заполните профиль',
        description: 'Название, логотип, адрес и часовой пояс — эти данные попадут в чеки и меню.',
        icon: <Store size={18} />,
        done: profileDone,
        actions: [{ label: 'Заполнить профиль', to: '/settings', primary: true }],
      },
      {
        id: 'menu',
        title: 'Добавьте категории и блюда',
        description:
          'Сначала создайте категорию — без неё блюдо не добавить. Затем заполните блюда вручную или сканером меню.',
        icon: <Utensils size={18} />,
        done: menuDone,
        checks: [
          { label: catCount > 0 ? `Категории · ${catCount}` : 'Категории', done: catCount > 0 },
          { label: dishCount > 0 ? `Блюда · ${dishCount}` : 'Блюда', done: dishCount > 0 },
        ],
        actions: menuActions,
      },
      {
        id: 'device',
        title: 'Подключите кассу',
        description:
          'Чтобы скачать кассу, установите приложение «Resstro» на ваш iPad. Для Android — скачайте напрямую с сайта. Затем привяжите устройство кодом из админки.',
        icon: <Tablet size={18} />,
        done: deviceDone || deviceVisited,
        actions: [
          {
            label: 'Подключить устройство',
            to: '/devices?pair=1',
            primary: true,
            onClick: markDeviceVisited,
          },
          { label: 'Скачать для Android', href: DOWNLOADS_URL },
        ],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEst, categories.data, dishes.data, devices.data, deviceVisited]);

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const allDone = doneCount === total;
  const pct = Math.round((doneCount / total) * 100);

  // ── Локальное состояние сворачивания/скрытия (per establishment) ──
  const collapseKey = currentEst ? lsKey(currentEst.id, 'collapsed') : '';
  const dismissKey = currentEst ? lsKey(currentEst.id, 'dismissed') : '';

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (!collapseKey) return false;
    return localStorage.getItem(collapseKey) === '1';
  });
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!dismissKey) return false;
    return localStorage.getItem(dismissKey) === '1';
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (collapseKey) localStorage.setItem(collapseKey, next ? '1' : '0');
      return next;
    });
  };

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  // Не показываем до загрузки данных (чтобы не мелькало), при ошибке,
  // если нет заведения или если пользователь скрыл завершённый роадмап.
  if (isLoading || isError || !currentEst) return null;
  if (dismissed) return null;

  // ── Всё готово: компактное поздравление с возможностью скрыть ──
  if (allDone) {
    return (
      <div className="card p-5 mb-6 bg-gradient-to-br from-emerald-50/80 to-transparent border-emerald-100">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-10 w-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center">
            <PartyPopper size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-emerald-800">Заведение готово к работе</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Все шаги запуска пройдены. Удачных продаж!
            </p>
          </div>
          <button
            onClick={dismiss}
            className="btn-ghost shrink-0 text-slate-500"
            title="Скрыть"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-5 mb-6 bg-gradient-to-br from-brand-50/70 to-transparent">
      {/* Заголовок + прогресс */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900">
            {doneCount === 0 ? 'Добро пожаловать в Resstro' : 'Запуск заведения'}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {doneCount === 0
              ? 'Три шага — и можно принимать заказы.'
              : `Готово ${doneCount} из ${total} — продолжим настройку.`}
          </p>
        </div>
        <button
          onClick={toggleCollapsed}
          className="btn-ghost shrink-0 text-slate-500"
          title={collapsed ? 'Развернуть' : 'Свернуть'}
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
      </div>

      {/* Прогресс-бар */}
      <div className="mt-3 flex items-center gap-3">
        <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs font-medium text-slate-500 tabular-nums shrink-0">{pct}%</span>
      </div>

      {/* Шаги */}
      {!collapsed && (
        <ul className="mt-4 divide-y divide-slate-100">
          {steps.map((step, idx) => (
            <StepRow key={step.id} step={step} index={idx} />
          ))}
        </ul>
      )}
    </div>
  );
}

function StepRow({ step, index }: { step: Step; index: number }) {
  return (
    <li className="flex items-start gap-4 py-3.5 first:pt-1">
      {/* Индикатор статуса */}
      <div
        className={
          'shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold ' +
          (step.done
            ? 'bg-emerald-500 text-white'
            : 'bg-brand-50 text-brand-600 ring-1 ring-brand-100')
        }
      >
        {step.done ? <Check size={16} strokeWidth={2.5} /> : index + 1}
      </div>

      {/* Текст + действия */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-brand-500/70">{step.icon}</span>
          <span
            className={
              'font-medium ' + (step.done ? 'text-slate-400 line-through' : 'text-slate-900')
            }
          >
            {step.title}
          </span>
        </div>

        {/* Под-статусы (например: Категории · 3 / Блюда · 0) */}
        {step.checks && step.checks.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {step.checks.map((c) => (
              <span
                key={c.label}
                className={
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ' +
                  (c.done
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-slate-100 text-slate-500')
                }
              >
                <Check
                  size={11}
                  strokeWidth={2.5}
                  className={c.done ? 'text-emerald-600' : 'text-slate-300'}
                />
                {c.label}
              </span>
            ))}
          </div>
        )}

        {!step.done && (
          <>
            <p className="text-sm text-slate-500 mt-1.5">{step.description}</p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {step.actions.map((a) =>
                a.href ? (
                  <a
                    key={a.label}
                    href={a.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={a.onClick}
                    className={a.primary ? 'btn-primary' : 'btn-outline'}
                  >
                    {a.primary ? null : <Download size={14} />}
                    {a.label}
                  </a>
                ) : (
                  <Link
                    key={a.label}
                    to={a.to ?? '/'}
                    onClick={a.onClick}
                    className={a.primary ? 'btn-primary' : 'btn-outline'}
                  >
                    {a.label}
                    {a.primary && <ArrowRight size={14} />}
                  </Link>
                ),
              )}
            </div>
          </>
        )}
      </div>
    </li>
  );
}
